export interface Task {
  taskDetails: TaskDetails;
  attributes: Attributes;
  assignees: Assignee[];
  comments?: GeneralTaskComment[];
  attachments?: GeneralTaskAttachment[];
  parentTask?: boolean;
  subtasks?: RelatedTaskInfo[];
  observers?: number[];
  escalationDetails?: EscalationDetails;
}

export interface TaskV2 extends AudiData {
  taskId?: number;
  title?: string;
  description?: string;
  status?: TaskStatusDB;
  dueDate?: string;
  priority?: TaskPriority;
  ownerId?: number;
  loanId?: number;
  propertyId?: number;
  isSystemTask?: boolean;
  category?: string;
  taskQueueStatus?: string;
  taskAssignees?: TaskAssigneeV2Response[];
  taskTags?: TaskTagV2Response[];
  taskWatchers?: TaskWatcherV2Response[];
  taskNotes?: TaskNote[];
  escalated?: boolean;
  ownerName?: string;
  assignedName?: string,
  parentTaskId?: number;
  parentTask?: boolean;
  observers?: number[];
  isInternal?: boolean;
  timeElapsed?: number;
  type1?: string;
  typeId1?: number;
  type2?: string;
  typeId2?: number;
  information?: string;
  templateId?: number;
  taskCode?: string;
  statusUpdateReason?: string;
  taskDocuments?: TaskDocumentV2[];
  ownerFirstName?: string;
  ownerLastName?: string;
  readOnly?: boolean;
}

export interface SaveTaskV2Request {
  taskDetails?: TaskV2;
  assignees?: Assignee[];
  observers?: number[];
  chatroomEmailSync?: boolean;
  isInternal?: boolean;
}

export interface UpdateTaskStatusAndAssigneeRequest {
  taskId?: number;
  taskStatus?: string;
  newAssigneeId?: number;
  reason?: string;
  comment?: string;
}

export interface UpdateReadOnlyTaskStatusAndAssigneeRequest {
  taskId: number;
  taskStatus: string;
  newAssigneeId: number;
  comment: string;
}

interface AudiData {
  createdBy?: string;
  createdDate?: string;
  lastUpdatedBy?: string;
  lastUpdatedDate?: string;
}

export interface EscalationDetails extends AudiData {
  taskId: number;
  userId: number;
}

export interface Assignee extends AudiData {
  taskAssigneeId?: number;
  taskId?: number;
  assigneeId?: number;
  assigneeFistName?: string;
  assigneeLastName?: string;
  assigneeStartDate?: string;
  assigneeEndDate?: string;
}

export interface Watcher extends AudiData {
  taskWatcherId?: number;
  taskId?: number;
  userId?: number;
}

export interface Attributes extends AudiData {
  taskId: number;
  loanId: number;
  propertyAppraisalId: number;
  taskType: string;
  borrowerId: number;
  propertyId: number;
  loanTaskId: number;
}

export enum TaskV2Type {
  LOAN_ID = "LOAN_ID",
  BORROWER_ID = "BORROWER_ID",
  PROPERTY_ID = "PROPERTY_ID",
  BORROWER_ENTITY_ID = "BORROWER_ENTITY_ID",
  EXTENSION_ID = "EXTENSION_ID",
  PAYOFF_ID = "PAYOFF_ID",
  DRAW_ID = "DRAW_ID",
  LEAD_ID = "LEAD_ID",
  CONTACT_ID = "CONTACT_ID",
  INSPECTION_ID = "INSPECTION_ID",
  CADENCE_CONTACT_ID = "CADENCE_CONTACT_ID",
  EMAIL_TO_TASK_MAPPING_ID = "EMAIL_TO_TASK_MAPPING_ID",
  TECH_SUPPORT = "TECH_SUPPORT" //Separate CATEWGORY
}

export interface TaskDetails extends AudiData {
  taskId?: number;
  parentTaskId?: number;
  title?: string;
  description?: string;
  status?: TaskStatus;
  dueDate?: string;
  priority?: TaskPriority;
  ownerId?: number;
  isSystemTask?: boolean;
  ownerFirstName?: string;
  ownerLastName?: string;
  category?: string;
  type1?: string;
  typeId1?: number;
  type2?: string;
  typeId2?: number;
  information?: string;
  templateId?: number;
  taskCode?: string;
  statusUpdateReason?: string;
}

export interface GeneralTaskComment {
  authorId: number;
  text: string;
  createdDate: string;
}

export interface TaskCategory {
  createdBy: string;
  createdDate: string;
  lastUpdatedBy: string | null;
  lastUpdatedDate: string | null;
  taskCategoryId: number;
  category: string;
  loanProcess: string;
  propertySection: boolean;
  borrowerSection: boolean;
  entitySection: boolean;
  active: boolean;
  displayName: string;
  displayOrder: number;
  typeId2: number;
}

export interface TaskNote {
  taskNoteId: number;
  taskId: number;
  comment: string;
  commentByUsername: string;
  commentDate: string;
}

export interface GeneralTaskAttachment {
  taskAttachmentId: number;
  taskId: number;
  documentId: number;
  attachmentName: string;
}

export interface TaskDocumentV2 extends AudiData {
  taskDocumentId: number;
  taskId: number;
  originalFileName: string;
  path?: string;
  docType?: string;
  lastUploadBy?: string;
  lastUploadDate?: string;
  firstUploadDate?: string;
  movedToExternalFileSystem?: boolean;
  lastAcceptedBy?: string;
  lastAcceptedDate?: string;
}

export interface TaskDocumentDownloadResponse {
  taskDocumentId: number;
  originalFileName: string;
  path?: string;
  contentType?: string;
  docBytesArray: string | number[];
}

export interface RelatedTaskInfo {
  taskId?: number;
  title?: string;
  status?: string;
}

export enum TaskStatus {
  PENDING = 'Pending',
  MORE_INFO_NEEDED = 'More Info Needed',
  BEING_REVIEWED = 'Being Reviewed',
  ACCEPTED = 'Accepted',
  NOT_APPLICABLE = 'Not Applicable',
  INACTIVE = 'Inactive',
}

export enum TaskStatusDB {
  PENDING = 'PENDING',
  MORE_INFO_NEEDED = 'MORE_INFO_NEEDED',
  BEING_REVIEWED = 'BEING_REVIEWED',
  ACCEPTED = 'ACCEPTED',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
  INACTIVE = 'INACTIVE',
}

export enum TaskPriority {
  HIGH = 'HIGH',
  MEDIUM = 'MEDIUM',
  LOW = 'LOW',
  CRITICAL = 'CRITICAL',
  VERY_CRITICAL = 'VERY_CRITICAL',
}

export interface TaskGridRow {
  lastUpdatedDate: string;
  description: string;
  assignees: string;
  ownerFirstName: null;
  title: string;
  ownerId: number;
  taskId: number;
  parentTaskId: number;
  document: any;
  loanTaskId: number;
  loanId: number;
  status: string;
  escalated: string;
  hasClosingCondition?: boolean;
  hasPostClosingCondition?: boolean;
}

export interface TaskV2Response extends AudiData {
  taskId: number;
  parentTaskId: number;
  title: string;
  description: string;
  status: string;
  priority: string;
  ownerId: number;
  createdBy: string;
  taskAssignees: TaskAssigneeV2Response[];
  taskWatchers: TaskWatcherV2Response[];
  taskTags: TaskTagV2Response[];
  type1?: string;
  typeId1?: number;
  type2?: string;
  typeId2?: number;
  information?: string;
  templateId?: number;
  taskCode?: string;
  statusUpdateReason?: string;
  category?: string;
}

export interface TaskAssigneeV2Response extends AudiData {
  taskAssigneeId?: number;
  taskId?: number;
  assigneeId?: number;
  active?: boolean;
  ownerName?: string;
}

export interface TaskWatcherV2Response extends AudiData {
  taskWatcherId: number;
  userId: number;
  taskId: number;
}

export interface TaskTagV2Response extends AudiData {
  taskTagId: number;
  taskId: number;
  tagType: string;
  tagValue: string;
}

export enum GeneralTasksView {
  KANBAN = 'KANBAN',
  LIST = 'LIST',
}

export enum GeneralTasksTab {
  ALL_TASKS = 'ALL_TASKS',
  ALL_TASKS_FOR_LOAN = 'ALL_TASKS_FOR_LOAN',
  MY_TASKS = 'MY_TASKS',
  MY_TEAM_TASKS = 'MY_TEAM_TASKS',
  MY_LOAN_TASKS_GROUPED = 'MY_LOAN_TASKS_GROUPED',
  MY_OTHER_TASKS = 'MY_OTHER_TASKS',
}

export enum GeneralTasksFilters {
  STATUS = 'STATUS',
  PRIORITY = 'PRIORITY',
  DUE_DATE = 'DUE_DATE',
  TITLE = 'TITLE',
  ASSIGNEE = 'ASSIGNEE',
  LOAN_ID = 'LOAN_ID',
  OWNER = 'OWNER',
  CREATED_BY = 'CREATED_BY',
  OPEN_SUBTASKS = 'OPEN_SUBTASKS',
  TASK_TYPE = 'TASK_TYPE',
  TASK_SCOPE = 'TASK_SCOPE',
  TASK_ID = 'TASK_ID',
  RESULT_TYPE = 'RESULT_TYPE',
}

export enum GeneralLoanTasksFilters {
  SECTION = 'SECTION',
  TASK_NAME = 'TASK_NAME',
}

export enum GeneralTaskType {
  CREDIT_REVIEW = 'CREDIT_REVIEW',
  BACKGROUND_REVIEW = 'BACKGROUND_REVIEW',
  TRACK_RECORD_REVIEW = 'TRACK_RECORD_REVIEW',
  APPRAISAL_REPORT_AND_RELATED = 'APPRAISAL_REPORT_AND_RELATED',
  APPRAISAL_RISK_REVIEW = 'APPRAISAL_RISK_REVIEW',
  COLLATERAL_DOCS = 'COLLATERAL_DOCS',
  SOW_REVIEW = 'SOW_REVIEW',
  ENTITY_SECTION = 'ENTITY_SECTION',
  APPRAISAL_REVIEW = 'APPRAISAL_REVIEW',

  CLOSING_ASSISTANCE = 'CLOSING_ASSISTANCE',
  PAYOFF_REQUEST = 'PAYOFF_REQUEST',
  DRAW_REQUEST = 'DRAW_REQUEST',
  LOAN_EXTENSIONS = 'LOAN_EXTENSIONS',
  OTHERS = 'OTHERS',
  CLOSING_CONDITIONS = 'CLOSING_CONDITIONS',
  LOAN_CLARIFICATIONS = 'LOAN_CLARIFICATIONS',
  ADDITIONAL_REQUIREMENT = 'ADDITIONAL_REQUIREMENT',

  SUBJECT_PROPERTY = 'SUBJECT_PROPERTY',
  TRACK_RECORD = 'TRACK_RECORD',
  CRM_SALES_REMINDER = 'CRM_SALES_REMINDER',
  FEEDBACK = 'FEEDBACK',
  TECH_SUPPORT = 'TECH_SUPPORT',
  EXTERNAL_TECH_SUPPORT = 'EXTERNAL_TECH_SUPPORT',
  INSURANCE = 'INSURANCE',
  INSURANCE_DRAWS = 'INSURANCE_DRAWS',
  INSURANCE_SERVICING = 'INSURANCE_SERVICING',
  LOAN_DOCUMENTS = 'LOAN_DOCUMENTS',

  ESCALATION = 'ESCALATION',

  APPRAISAL_REPORT_REVIEW = 'APPRAISAL_REPORT_REVIEW',
  CMT_REVIEW = 'CMT_REVIEW',
  APPRAISAL_DOCUMENT_COLLECTION = 'APPRAISAL_DOCUMENT_COLLECTION',
  SERVICING = 'SERVICING',
  PRESCREEN = 'PRESCREEN',
  TITLE = 'TITLE',
  BORROWER_CLOSING_AGENT = 'BORROWER_CLOSING_AGENT',
  LOAN_TERMS = 'LOAN_TERMS',
  CORPORATE_DOCUMENTS = 'CORPORATE_DOCUMENTS',
  CLOSING_DOCUMENTS = 'CLOSING_DOCUMENTS',
  LOAN_RELATED_DOCUMENTS = 'LOAN_RELATED_DOCUMENTS',
  SIGNED_CLOSING_DOCUMENTS = 'SIGNED_CLOSING_DOCUMENTS',
  COMMITTEE = 'COMMITTEE',
  APPRAISAL_ORDER = 'APPRAISAL_ORDER',
  GENERATED_CLOSING_DOCUMENTS = 'GENERATED_CLOSING_DOCUMENTS',
  SECOND_APPRAISAL_ORDER = 'SECOND_APPRAISAL_ORDER',
  BOARDING = 'BOARDING',
  SCANNED_CLOSING_DOCS = 'SCANNED_CLOSING_DOCS',
  PHYSICAL_ORIGINAL_RECORDED_CLOSING_DOCS = 'PHYSICAL_ORIGINAL_RECORDED_CLOSING_DOCS',
  DRAW_INFORMATION = 'DRAW_INFORMATION',
  APPRAISAL_AND_SOW = 'APPRAISAL_AND_SOW',
  INSPECTION = 'INSPECTION',
  WIRE = 'WIRE',
  PENDING_INSURANCE = 'PENDING_INSURANCE',
  INTERNAL = 'INTERNAL',
  POST_CLOSING = 'POST_CLOSING',
  E_FILE_ONLY_RECORDED_CLOSING_DOCS = 'E_FILE_ONLY_RECORDED_CLOSING_DOCS',
  MATURITY = 'MATURITY',
  DELINQUENCY_MANAGEMENT = 'DELINQUENCY_MANAGEMENT',
  INTERNAL_PAYOFF_REQUEST = 'INTERNAL_PAYOFF_REQUEST',
  PAYOFF_DETAILS = 'PAYOFF_DETAILS',
  EXTENSION_DOCUMENTS = 'EXTENSION_DOCUMENTS',
  PROTECTIVE_ADVANCES = 'PROTECTIVE_ADVANCES',
  INTERNAL_DOCUMENTS = 'INTERNAL_DOCUMENTS',
  GENERATED_CEMA_DOCUMENTS = 'GENERATED_CEMA_DOCUMENTS',
  BORROWER_REVIEW = 'BORROWER_REVIEW',
  TRAILING_DOCUMENTS = 'TRAILING_DOCUMENTS',
  FORECLOSURE_MILESTONES = 'FORECLOSURE_MILESTONES',
  VALUATION = 'VALUATION',
  UNDERWRITING = 'UNDERWRITING',
}

export enum TaskScope {
  REPORTED_BY_ME = 'REPORTED_BY_ME',
  WATCHED_BY_ME = 'WATCHED_BY_ME',
  ASSIGNED_TO_ME = 'ASSIGNED_TO_ME',
  TEAM = 'MY_TEAM',
}

export enum ResultType {
  ALL_TASKS = 'ALL_TASKS',
  LOAN_TASKS = 'LOAN_TASKS',
  MANUAL_TASKS = 'MANUAL_TASKS',
}

export interface PopulateTaskFormField {
  title?: string;
  priority?: string;
  assigneeId?: number;
  taskType?: GeneralTaskType;
  loanId?: number;
}

export enum TaskTypeForm {
  LOAN_TASK = 'Loan Task',
  TECH_SUPPORT = 'Tech Support',
  GENERAL_TASK = 'General Task'
}

export const GENERAL_TASK_CODE = 'General Task';

export enum TaskScopeForm {
  INTERNAL = 'Internal Team',
  EXTERNAL = 'External Team',
}

export enum TaskV2Category {
  ADDITIONAL_REQUIREMENT = 'ADDITIONAL_REQUIREMENT',
  APPRAISAL_AND_SOW = 'APPRAISAL_AND_SOW',
  APPRAISAL_DOCUMENT_COLLECTION = 'APPRAISAL_DOCUMENT_COLLECTION',
  APPRAISAL_ORDER = 'APPRAISAL_ORDER',
  APPRAISAL_REPORT_AND_RELATED = 'APPRAISAL_REPORT_AND_RELATED',
  APPRAISAL_REPORT_REVIEW = 'APPRAISAL_REPORT_REVIEW',
  APPRAISAL_REVIEW = 'APPRAISAL_REVIEW',
  APPRAISAL_RISK_REVIEW = 'APPRAISAL_RISK_REVIEW',
  BACKGROUND_REVIEW = 'BACKGROUND_REVIEW',
  BOARDING = 'BOARDING',
  BORROWER_CLOSING_AGENT = 'BORROWER_CLOSING_AGENT',
  BORROWER_REVIEW = 'BORROWER_REVIEW',
  CADENCE = 'CADENCE',
  CLOSING_ASSISTANCE = 'CLOSING_ASSISTANCE',
  CLOSING_CONDITIONS = 'CLOSING_CONDITIONS',
  CLOSING_DOCUMENTS = 'CLOSING_DOCUMENTS',
  CMT_REVIEW = 'CMT_REVIEW',
  COLLATERAL_DOCS = 'COLLATERAL_DOCS',
  COMMITTEE = 'COMMITTEE',
  CORPORATE_DOCUMENTS = 'CORPORATE_DOCUMENTS',
  CREDIT_REVIEW = 'CREDIT_REVIEW',
  CRM_SALES_REMINDER = 'CRM_SALES_REMINDER',
  DELINQUENCY_MANAGEMENT = 'DELINQUENCY_MANAGEMENT',
  DRAW_INFORMATION = 'DRAW_INFORMATION',
  DRAW_REQUEST = 'DRAW_REQUEST',
  E_FILE_ONLY_RECORDED_CLOSING_DOCS = 'E_FILE_ONLY_RECORDED_CLOSING_DOCS',
  ENTITY_SECTION = 'ENTITY_SECTION',
  ESCALATION = 'ESCALATION',
  EXTENSION_DOCUMENTS = 'EXTENSION_DOCUMENTS',
  EXTERNAL_TECH_SUPPORT = 'EXTERNAL_TECH_SUPPORT',
  FEEDBACK = 'FEEDBACK',
  FLOOD_CERT = 'FLOOD_CERT',
  FORECLOSURE_MILESTONES = 'FORECLOSURE_MILESTONES',
  GENERATED_CEMA_DOCUMENTS = 'GENERATED_CEMA_DOCUMENTS',
  GENERATED_CLOSING_DOCUMENTS = 'GENERATED_CLOSING_DOCUMENTS',
  INSPECTION = 'INSPECTION',
  INSURANCE = 'INSURANCE',
  INSURANCE_DRAWS = 'INSURANCE_DRAWS',
  INTERNAL = 'INTERNAL',
  INTERNAL_DOCUMENTS = 'INTERNAL_DOCUMENTS',
  INTERNAL_PAYOFF_REQUEST = 'INTERNAL_PAYOFF_REQUEST',
  LOAN_CLARIFICATIONS = 'LOAN_CLARIFICATIONS',
  LOAN_DOCUMENTS = 'LOAN_DOCUMENTS',
  LOAN_EXTENSIONS = 'LOAN_EXTENSIONS',
  LOAN_RELATED_DOCUMENTS = 'LOAN_RELATED_DOCUMENTS',
  LOAN_TERMS = 'LOAN_TERMS',
  MATURITY = 'MATURITY',
  OTHERS = 'OTHERS',
  PAYOFF_DETAILS = 'PAYOFF_DETAILS',
  PAYOFF_REQUEST = 'PAYOFF_REQUEST',
  PENDING_INSURANCE = 'PENDING_INSURANCE',
  PHYSICAL_ORIGINAL_RECORDED_CLOSING_DOCS = 'PHYSICAL_ORIGINAL_RECORDED_CLOSING_DOCS',
  POST_CLOSING = 'POST_CLOSING',
  PRESCREEN = 'PRESCREEN',
  PROTECTIVE_ADVANCES = 'PROTECTIVE_ADVANCES',
  REFER_A_FRIEND = 'REFER_A_FRIEND',
  SCANNED_CLOSING_DOCS = 'SCANNED_CLOSING_DOCS',
  SECOND_APPRAISAL_ORDER = 'SECOND_APPRAISAL_ORDER',
  SERVICING = 'SERVICING',
  SIGNED_CLOSING_DOCUMENTS = 'SIGNED_CLOSING_DOCUMENTS',
  SOW_REVIEW = 'SOW_REVIEW',
  SUBJECT_PROPERTY = 'SUBJECT_PROPERTY',
  TECH_SUPPORT = 'TECH_SUPPORT',
  TITLE = 'TITLE',
  TRACK_RECORD = 'TRACK_RECORD',
  TRACK_RECORD_REVIEW = 'TRACK_RECORD_REVIEW',
  TRAILING_DOCUMENTS = 'TRAILING_DOCUMENTS',
  UNDERWRITING = 'UNDERWRITING',
  VALUATION = 'VALUATION',
  WIRE = 'WIRE'
}
